import { useTranslation } from "react-i18next";
import { ShieldCheck, Lightning, GlobeSimple, DeviceMobile, ArrowRight } from "@phosphor-icons/react";
import SEOHead from "../components/SEOHead";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.luminawallet.app";
const APP_STORE_URL = "https://apps.apple.com/us/app/lumina-wallet/id6760651380";

const DownloadPage = () => {
  const { t } = useTranslation();

  return (
    <main role="main" aria-label="Page de téléchargement Lumina Wallet" className="min-h-screen bg-lumina-dark pt-20">
      <SEOHead 
        title={t("download.seoTitle")}
        description={t("download.seoDescription")}
        path="/download"
      />
      
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-lumina-violet/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-600/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-lumina-violet/10 rounded-full blur-3xl" />
      </div>

      <section className="relative py-12 md:py-20 overflow-hidden" aria-labelledby="download-title">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <header className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-lumina-violet/10 border border-lumina-violet/30 rounded-full mb-6">
              <Lightning size={20} weight="duotone" className="text-lumina-violet" aria-hidden="true" />
              <span className="text-lumina-violet text-sm font-medium">{t("download.badge")}</span>
            </div>
            <h1 id="download-title" className="text-4xl md:text-6xl font-bold text-white mb-6">
              {t("download.title")} <span className="bg-gradient-to-r from-lumina-violet to-purple-400 bg-clip-text text-transparent">{t("download.titleHighlight")}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">{t("download.subtitle")}</p>
          </header>

          {/* Store Buttons — Main Section */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            
            {/* Google Play — Active */}
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 rounded-3xl p-8 hover:border-lumina-violet/50 transition-all duration-300 hover:shadow-xl hover:shadow-lumina-violet/10 cursor-pointer"
              data-testid="download-google-play-card"
            >
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-green-500/20 border border-green-500/40 rounded-full text-green-400 text-xs font-semibold">{t("download.available")}</span>
              </div>
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 bg-[#000000] rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <svg viewBox="0 0 512 512" className="w-12 h-12">
                    <path d="M270.1 257.3L34.4 501.7c-2.7-4.4-4.4-9.5-4.4-15.2V25.5c0-5.7 1.6-10.8 4.4-15.2l235.7 247z" fill="#4DB6AC"/>
                    <path d="M366.4 332.7L270.1 257.3 34.4 11.7c6.9-11.2 19.7-15.5 31.9-8.5l300.1 173.3-0 156.2z" fill="#DCE775"/>
                    <path d="M366.4 332.7L270.1 257.3l96.3-80.8 110.3 63.6c17.8 10.3 17.8 36.1 0 46.4L366.4 332.7z" fill="#F44336"/>
                    <path d="M366.4 332.7l-300.1 173.3c-12.2 7-25-2.7-31.9-8.5l235.7-240.2 96.3 75.4z" fill="#F06292"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">Google Play</h2>
                  <p className="text-gray-400 text-sm">Android 8.0+</p>
                </div>
                <div className="flex items-center gap-3 px-6 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-semibold group-hover:bg-white/15 group-hover:border-white/30 transition-all">
                  <svg viewBox="0 0 512 512" className="w-5 h-5 flex-shrink-0">
                    <path d="M270.1 257.3L34.4 501.7c-2.7-4.4-4.4-9.5-4.4-15.2V25.5c0-5.7 1.6-10.8 4.4-15.2l235.7 247z" fill="#4DB6AC"/>
                    <path d="M366.4 332.7L270.1 257.3 34.4 11.7c6.9-11.2 19.7-15.5 31.9-8.5l300.1 173.3-0 156.2z" fill="#DCE775"/>
                    <path d="M366.4 332.7L270.1 257.3l96.3-80.8 110.3 63.6c17.8 10.3 17.8 36.1 0 46.4L366.4 332.7z" fill="#F44336"/>
                    <path d="M366.4 332.7l-300.1 173.3c-12.2 7-25-2.7-31.9-8.5l235.7-240.2 96.3 75.4z" fill="#F06292"/>
                  </svg>
                  <span>{t("download.getOnGooglePlay")}</span>
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </a>

            {/* App Store — Active */}
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 rounded-3xl p-8 hover:border-lumina-violet/50 transition-all duration-300 hover:shadow-xl hover:shadow-lumina-violet/10 cursor-pointer"
              data-testid="download-app-store-card"
            >
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-green-500/20 border border-green-500/40 rounded-full text-green-400 text-xs font-semibold">{t("download.available")}</span>
              </div>
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 bg-[#000000] rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <svg viewBox="0 0 24 24" className="w-10 h-10" fill="white">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">App Store</h2>
                  <p className="text-gray-400 text-sm">iOS 15+</p>
                </div>
                <div className="flex items-center gap-3 px-6 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-semibold group-hover:bg-white/15 group-hover:border-white/30 transition-all">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span>{t("download.getOnAppStore")}</span>
                  <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </a>
          </div>

          {/* Bottom Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: t("download.featuresBottom.secure.title"), desc: t("download.featuresBottom.secure.desc"), color: "text-lumina-violet", glowHex: "rgba(139,92,246,0.4)" },
              { icon: Lightning, title: t("download.featuresBottom.fast.title"), desc: t("download.featuresBottom.fast.desc"), color: "text-purple-400", glowHex: "rgba(168,85,247,0.4)" },
              { icon: GlobeSimple, title: t("download.featuresBottom.multichain.title"), desc: t("download.featuresBottom.multichain.desc"), color: "text-lumina-violet", glowHex: "rgba(139,92,246,0.4)" }
            ].map((item) => (
              <div key={item.title} className="p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-lumina-violet/30 transition-all group">
                <div className="mb-4 group-hover:scale-110 transition-transform">
                  <item.icon size={28} weight="duotone" className={item.color} style={{ filter: `drop-shadow(0 0 10px ${item.glowHex})` }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default DownloadPage;
